import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, TreePine, ChevronRight, Building, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const Registration = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [scrolled, setScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-sky-50">
      <Navbar scrolled={scrolled} />

      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? 'SummitFlow Kayıt' : 'SummitFlow Registration'}
          </h1>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'Lütfen yer almak istediğiniz alanı seçiniz' : 'Please select your zone'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-sky-200 hover:border-sky-400 shadow-lg rounded-3xl overflow-hidden cursor-pointer transition-all hover:shadow-2xl group" onClick={() => navigate('/kayit/blue-zone')}>
              <div className="relative h-64 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1740441155431-1d09cdb2de01" alt="Blue Zone" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <Shield className="h-12 w-12 text-white mb-2" />
                </div>
              </div>
              <CardHeader className="pb-4">
                <CardTitle className="text-3xl text-sky-700 mb-2">Blue Zone</CardTitle>
                <p className="text-gray-600 text-base leading-relaxed">
                  {language === 'tr' ? 'Resmi delege katılım alanı. Hükümet delegeleri, gözlemci kuruluşlar, medya ve akredite katılımcılar için.' : 'Official delegate participation area. For government delegates, observer organizations, media, and accredited participants.'}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Resmi müzakerelere katılım' : 'Participation in official negotiations'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Ülke pavilyonlarına erişim' : 'Access to country pavilions'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Yan etkinlikler ve workshoplar' : 'Side events and workshops'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Akreditasyon gerekli' : 'Accreditation required'}</span>
                  </div>
                </div>
                <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full py-6 text-lg group">
                  {language === 'tr' ? 'Blue Zone Kayıt' : 'Register for Blue Zone'}
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-200 hover:border-emerald-400 shadow-lg rounded-3xl overflow-hidden cursor-pointer transition-all hover:shadow-2xl group" onClick={() => navigate('/kayit/green-zone')}>
              <div className="relative h-64 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1594080051162-74b97d619668" alt="Green Zone" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <TreePine className="h-12 w-12 text-white mb-2" />
                </div>
              </div>
              <CardHeader className="pb-4">
                <CardTitle className="text-3xl text-emerald-700 mb-2">Green Zone</CardTitle>
                <p className="text-gray-600 text-base leading-relaxed">
                  {language === 'tr' ? 'Halka açık alan. Sivil toplum, girişimciler, gençlik, yerli halklar ve genel katılımcılar için.' : 'Public zone. For civil society, entrepreneurs, youth, indigenous peoples, and general participants.'}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Halka açık etkinlikler' : 'Public events'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Kültürel programlar' : 'Cultural programs'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Topluluk etkinlikleri' : 'Community events'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{language === 'tr' ? 'Ücretsiz giriş' : 'Free entry'}</span>
                  </div>
                </div>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-6 text-lg group">
                  {language === 'tr' ? 'Green Zone Kayıt' : 'Register for Green Zone'}
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-16 bg-gradient-to-br from-emerald-50 to-sky-50 rounded-3xl p-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              {language === 'tr' ? 'Kayıt Hakkında' : 'About Registration'}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6">
                <Building className="h-8 w-8 text-sky-600 mb-3" />
                <h4 className="font-bold text-lg text-gray-900 mb-2">
                  {language === 'tr' ? 'Kurumsal Kayıt' : 'Organizational Registration'}
                </h4>
                <p className="text-gray-600">
                  {language === 'tr' ? 'Hükümetler, STKlar, özel sektör ve medya için resmi akreditasyon süreci' : 'Official accreditation process for governments, NGOs, private sector, and media'}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6">
                <Users className="h-8 w-8 text-emerald-600 mb-3" />
                <h4 className="font-bold text-lg text-gray-900 mb-2">
                  {language === 'tr' ? 'Bireysel Kayıt' : 'Individual Registration'}
                </h4>
                <p className="text-gray-600">
                  {language === 'tr' ? 'Green Zone etkinliklerine katılmak isteyen bireyler için basit kayıt' : 'Simple registration for individuals wishing to participate in Green Zone events'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Registration;